/**
 * Phase 2 MM9 — studio i18n (separate typed module so the giant
 * UiStrings interface stays untouched). All four locales, real
 * translations, honest phase labels.
 */

import type { LocaleCode } from "./locales";

export interface StudioStrings {
  prospectsTitle: string;
  projectsTitle: string;
  newProspect: string;
  newProject: string;
  nameLabel: string;
  typeLabel: string;
  statusLabel: string;
  countryLabel: string;
  cityLabel: string;
  notesLabel: string;
  save: string;
  archive: string;
  searchLabel: string;
  emptyList: string;
  loadError: string;
  permissionDenied: string;
  briefTab: string;
  referencesTab: string;
  generationTab: string;
  activityTab: string;
  overviewTab: string;
  completeness: string;
  missingFields: string;
  uploadReference: string;
  referenceType: string;
  createGenerationBrief: string;
  readiness: string;
  noImagePhase: string;
  nextAction: string;
  teamTitle: string;
  inviteMember: string;
  roleLabel: string;
  providerLabel: string;
  suspend: string;
  reactivate: string;
  googleLogin: string;
  googleUnavailable: string;
  visualsTab: string;
  newConceptSet: string;
  generateImages: string;
  imageCount: string;
  objectType: string;
  conceptDirection: string;
  styleDirection: string;
  disabledNotice: string;
  mockNotice: string;
  conceptPreviewOnly: string;
  selectConcept: string;
  rejectConcept: string;
  exportPack: string;
  costEstimate: string;
  artworkTab: string;
  newArtworkPackage: string;
  packageType: string;
  completenessScore: string;
  missingItems: string;
  panelsSection: string;
  copySection: string;
  dielineSection: string;
  checklistSection: string;
  exportsSection: string;
  uploadDieline: string;
  notPrintReady: string;
  complianceTab: string;
  newComplianceCase: string;
  reviewType: string;
  finalGate: string;
  riskLevel: string;
  claimsSection: string;
  languageSection: string;
  signoffSection: string;
  gateSection: string;
  requestSignoff: string;
  notLegalApproval: string;
  suppliersTitle: string;
  supplierTab: string;
  newSupplier: string;
  newRfq: string;
  readiness: string;
  shortlist: string;
  quotesSection: string;
  comparisonSection: string;
  recommendation: string;
  humanDecision: string;
  notSupplierApproval: string;
  fitScore: string;
  salesTab: string;
  newSalesPackage: string;
  salesSections: string;
  pricingBlocks: string;
  reviewGate: string;
  exportInternal: string;
  exportClientPreview: string;
  exportCaseStudy: string;
  clientPreviewSafety: string;
  notCustomerApproval: string;
  readinessScore: string;
  productionTab: string;
  createWorkflow: string;
  qaChecklist: string;
  qaGate: string;
  revisions: string;
  finalDelivery: string;
  ownerTestReady: string;
  realTeamTestPlan: string;
  internalWorkflowOnly: string;
  providerActivationPending: string;
  addRevision: string;
  approveOwnerTest: string;
}

const en: StudioStrings = {
  prospectsTitle: "Prospects", projectsTitle: "Packaging projects",
  newProspect: "New prospect", newProject: "New project",
  nameLabel: "Name", typeLabel: "Type", statusLabel: "Status",
  countryLabel: "Country", cityLabel: "City", notesLabel: "Notes",
  save: "Save", archive: "Archive", searchLabel: "Search",
  emptyList: "Nothing here yet — create the first one.",
  loadError: "Could not load data.", permissionDenied: "You do not have permission for this action.",
  briefTab: "Brief", referencesTab: "References", generationTab: "Generation brief",
  activityTab: "Activity", overviewTab: "Overview",
  completeness: "Brief completeness", missingFields: "Missing fields",
  uploadReference: "Upload reference", referenceType: "Reference type",
  createGenerationBrief: "Compile design generation brief",
  readiness: "Readiness", noImagePhase: "No image/mockup generation in this phase — this compiles the structured input for a future phase.",
  nextAction: "Next recommended action", teamTitle: "Internal team",
  inviteMember: "Add team member", roleLabel: "Role", providerLabel: "Login provider",
  suspend: "Suspend", reactivate: "Reactivate",
  googleLogin: "Continue with Google", googleUnavailable: "Google login is not configured — use email/password.",
  visualsTab: "Visual concepts", newConceptSet: "New concept set",
  generateImages: "Generate concepts", imageCount: "Images",
  objectType: "Packaging object", conceptDirection: "Concept direction",
  styleDirection: "Style direction",
  disabledNotice: "Image generation is disabled. The owner can enable mock or gated live mode.",
  mockNotice: "MOCK mode — placeholder previews only, NOT real AI images.",
  conceptPreviewOnly: "Concept previews only — NOT print-ready artwork. Human design & prepress review required.",
  selectConcept: "Select", rejectConcept: "Reject",
  exportPack: "Export preview pack", costEstimate: "Cost estimate",
  artworkTab: "Artwork / Print handoff", newArtworkPackage: "New artwork package",
  packageType: "Package type", completenessScore: "Completeness",
  missingItems: "Missing items", panelsSection: "Panel plan",
  copySection: "Copy blocks", dielineSection: "Dieline / templates",
  checklistSection: "Print checklist", exportsSection: "Exports",
  uploadDieline: "Upload dieline",
  notPrintReady: "Production-assist package — NOT automatically print-ready. Designer, prepress and supplier review required. No CAD-grade validation.",
  complianceTab: "Compliance / Approval", newComplianceCase: "New compliance case",
  reviewType: "Review type", finalGate: "Final gate", riskLevel: "Risk level",
  claimsSection: "Claims", languageSection: "Language", signoffSection: "Sign-off",
  gateSection: "Final gate", requestSignoff: "Request sign-off",
  notLegalApproval: "Internal decision-support + human sign-off. NOT legal advice, regulatory/food-safety certification, supplier or print-ready approval. Human review required.",
  suppliersTitle: "Supplier directory", supplierTab: "Suppliers / RFQ",
  newSupplier: "New supplier", newRfq: "Generate RFQ", readiness: "Readiness",
  shortlist: "Fit shortlist", quotesSection: "Quotes",
  comparisonSection: "Comparison", recommendation: "Recommendation draft",
  humanDecision: "Human decision", fitScore: "Fit",
  notSupplierApproval: "Internal directory + RFQ + quote comparison aid. NOT a supplier approval, procurement, contract or payment. Certifications are unverified metadata; recommendations are DRAFTS — human decision required.",
  salesTab: "Sales package", newSalesPackage: "Generate sales package",
  salesSections: "Sections", pricingBlocks: "Pricing placeholders",
  reviewGate: "Review gate", exportInternal: "Export internal",
  exportClientPreview: "Export client preview", exportCaseStudy: "Export case study",
  readinessScore: "Readiness",
  clientPreviewSafety: "Client preview is watermarked and excludes internal notes, margins and supplier details.",
  notCustomerApproval: "Internal professional proposal draft. CLIENT PREVIEW / CONCEPT ONLY — not print-ready, not a contract, not an invoice, not a customer/legal/supplier approval. Human review required.",
  productionTab: "Production / QA", createWorkflow: "Start production workflow",
  qaChecklist: "QA checklist", qaGate: "QA gate", revisions: "Revisions",
  finalDelivery: "Final delivery", ownerTestReady: "Owner test ready",
  realTeamTestPlan: "Real team test plan", addRevision: "Add revision",
  approveOwnerTest: "Approve for owner test",
  internalWorkflowOnly: "Internal QA workflow only — not a public/commercial launch, not a customer/contract/invoice/payment, not legal/regulatory/supplier/print-ready/production approval. Human review required.",
  providerActivationPending: "Provider activation is a separate, later, pending step — this plan does not activate any provider.",
};

const fa: StudioStrings = {
  prospectsTitle: "مشتریان بالقوه", projectsTitle: "پروژه‌های بسته‌بندی",
  newProspect: "مشتری بالقوه جدید", newProject: "پروژه جدید",
  nameLabel: "نام", typeLabel: "نوع", statusLabel: "وضعیت",
  countryLabel: "کشور", cityLabel: "شهر", notesLabel: "یادداشت",
  save: "ذخیره", archive: "بایگانی", searchLabel: "جستجو",
  emptyList: "هنوز موردی نیست — اولین مورد را بسازید.",
  loadError: "بارگذاری داده ناموفق بود.", permissionDenied: "برای این عمل مجوز ندارید.",
  briefTab: "بریف", referencesTab: "مراجع", generationTab: "بریف تولید طرح",
  activityTab: "فعالیت", overviewTab: "نمای کلی",
  completeness: "تکمیل بریف", missingFields: "فیلدهای ناقص",
  uploadReference: "بارگذاری مرجع", referenceType: "نوع مرجع",
  createGenerationBrief: "کامپایل بریف تولید طرح",
  readiness: "آمادگی", noImagePhase: "در این فاز تصویری تولید نمی‌شود — این فقط ورودی ساخت‌یافته برای فاز آینده است.",
  nextAction: "اقدام پیشنهادی بعدی", teamTitle: "تیم داخلی",
  inviteMember: "افزودن عضو تیم", roleLabel: "نقش", providerLabel: "روش ورود",
  suspend: "تعلیق", reactivate: "فعال‌سازی",
  googleLogin: "ورود با گوگل", googleUnavailable: "ورود گوگل پیکربندی نشده — از ایمیل/رمز استفاده کنید.",
  visualsTab: "کانسپت‌های بصری", newConceptSet: "مجموعه کانسپت جدید",
  generateImages: "تولید کانسپت‌ها", imageCount: "تعداد تصویر",
  objectType: "شیء بسته‌بندی", conceptDirection: "جهت کانسپت",
  styleDirection: "جهت سبک",
  disabledNotice: "تولید تصویر غیرفعال است. مالک می‌تواند حالت ماک یا لایو کنترل‌شده را فعال کند.",
  mockNotice: "حالت ماک — فقط پیش‌نمایش جایگزین، تصویر واقعی هوش مصنوعی نیست.",
  conceptPreviewOnly: "فقط پیش‌نمایش کانسپت — فایل چاپی نیست. بازبینی طراح و پیش‌چاپ الزامی است.",
  selectConcept: "انتخاب", rejectConcept: "رد",
  exportPack: "خروجی بسته پیش‌نمایش", costEstimate: "برآورد هزینه",
  artworkTab: "آرت‌ورک / تحویل چاپ", newArtworkPackage: "بسته آرت‌ورک جدید",
  packageType: "نوع بسته", completenessScore: "میزان تکمیل",
  missingItems: "موارد ناقص", panelsSection: "نقشه پنل‌ها",
  copySection: "بلوک‌های متن", dielineSection: "دای‌لاین / قالب‌ها",
  checklistSection: "چک‌لیست چاپ", exportsSection: "خروجی‌ها",
  uploadDieline: "بارگذاری دای‌لاین",
  notPrintReady: "بسته کمکی تولید — به‌صورت خودکار آماده چاپ نیست. بازبینی طراح، پیش‌چاپ و تأمین‌کننده الزامی است. اعتبارسنجی CAD انجام نمی‌شود.",
  complianceTab: "انطباق / تأیید", newComplianceCase: "پرونده انطباق جدید",
  reviewType: "نوع بازبینی", finalGate: "دروازه نهایی", riskLevel: "سطح ریسک",
  claimsSection: "ادعاها", languageSection: "زبان", signoffSection: "تأیید نهایی",
  gateSection: "دروازه نهایی", requestSignoff: "درخواست تأیید",
  notLegalApproval: "پشتیبانی تصمیم داخلی + تأیید انسانی. مشاوره حقوقی، گواهی قانونی/ایمنی غذا، تأیید تأمین‌کننده یا آماده‌چاپ نیست. بازبینی انسانی الزامی است.",
  suppliersTitle: "دایرکتوری تأمین‌کنندگان", supplierTab: "تأمین‌کننده / RFQ",
  newSupplier: "تأمین‌کننده جدید", newRfq: "ساخت RFQ", readiness: "آمادگی",
  shortlist: "فهرست تناسب", quotesSection: "قیمت‌ها",
  comparisonSection: "مقایسه", recommendation: "پیش‌نویس توصیه",
  humanDecision: "تصمیم انسانی", fitScore: "تناسب",
  notSupplierApproval: "ابزار دایرکتوری + RFQ + مقایسه قیمت داخلی. تأیید تأمین‌کننده، خرید، قرارداد یا پرداخت نیست. گواهی‌ها متادیتای تأییدنشده‌اند؛ توصیه‌ها پیش‌نویس‌اند — تصمیم انسانی الزامی است.",
  salesTab: "بستهٔ فروش", newSalesPackage: "ساخت بستهٔ فروش",
  salesSections: "بخش‌ها", pricingBlocks: "بلوک‌های قیمت (جای‌نما)",
  reviewGate: "دروازهٔ بازبینی", exportInternal: "خروجی داخلی",
  exportClientPreview: "خروجی پیش‌نمایش مشتری", exportCaseStudy: "خروجی مطالعهٔ موردی",
  readinessScore: "آمادگی",
  clientPreviewSafety: "پیش‌نمایش مشتری واترمارک دارد و یادداشت‌های داخلی، حاشیهٔ سود و جزئیات تأمین‌کننده را حذف می‌کند.",
  notCustomerApproval: "پیش‌نویس پیشنهاد حرفه‌ای داخلی. پیش‌نمایش مشتری / فقط کانسپت — آمادهٔ چاپ نیست، قرارداد نیست، فاکتور نیست، تأیید مشتری/حقوقی/تأمین‌کننده نیست. بازبینی انسانی الزامی است.",
  productionTab: "تولید / QA", createWorkflow: "شروع گردش‌کار تولید",
  qaChecklist: "چک‌لیست QA", qaGate: "دروازهٔ QA", revisions: "بازنگری‌ها",
  finalDelivery: "تحویل نهایی", ownerTestReady: "آمادهٔ تست مالک",
  realTeamTestPlan: "طرح تست تیم واقعی", addRevision: "افزودن بازنگری",
  approveOwnerTest: "تأیید برای تست مالک",
  internalWorkflowOnly: "فقط گردش‌کار QA داخلی — راه‌اندازی عمومی/تجاری نیست، تأیید مشتری/قرارداد/فاکتور/پرداخت نیست، تأیید حقوقی/قانونی/تأمین‌کننده/چاپ/تولید نیست. بازبینی انسانی الزامی است.",
  providerActivationPending: "فعال‌سازی ارائه‌دهنده مرحله‌ای جداگانه و بعدی است — این طرح هیچ ارائه‌دهنده‌ای را فعال نمی‌کند.",
};

const ar: StudioStrings = {
  prospectsTitle: "العملاء المحتملون", projectsTitle: "مشاريع التغليف",
  newProspect: "عميل محتمل جديد", newProject: "مشروع جديد",
  nameLabel: "الاسم", typeLabel: "النوع", statusLabel: "الحالة",
  countryLabel: "الدولة", cityLabel: "المدينة", notesLabel: "ملاحظات",
  save: "حفظ", archive: "أرشفة", searchLabel: "بحث",
  emptyList: "لا يوجد شيء بعد — أنشئ الأول.",
  loadError: "تعذر تحميل البيانات.", permissionDenied: "ليست لديك صلاحية لهذا الإجراء.",
  briefTab: "الموجز", referencesTab: "المراجع", generationTab: "موجز توليد التصميم",
  activityTab: "النشاط", overviewTab: "نظرة عامة",
  completeness: "اكتمال الموجز", missingFields: "حقول ناقصة",
  uploadReference: "رفع مرجع", referenceType: "نوع المرجع",
  createGenerationBrief: "تجميع موجز توليد التصميم",
  readiness: "الجاهزية", noImagePhase: "لا يُنتج أي تصميم/صورة في هذه المرحلة — هذا تجهيز مدخلات لمرحلة قادمة.",
  nextAction: "الإجراء الموصى به", teamTitle: "الفريق الداخلي",
  inviteMember: "إضافة عضو", roleLabel: "الدور", providerLabel: "طريقة الدخول",
  suspend: "تعليق", reactivate: "إعادة تفعيل",
  googleLogin: "المتابعة عبر Google", googleUnavailable: "دخول Google غير مُفعّل — استخدم البريد/كلمة المرور.",
  visualsTab: "المفاهيم البصرية", newConceptSet: "مجموعة مفاهيم جديدة",
  generateImages: "توليد المفاهيم", imageCount: "عدد الصور",
  objectType: "عنصر التغليف", conceptDirection: "اتجاه المفهوم",
  styleDirection: "اتجاه الأسلوب",
  disabledNotice: "توليد الصور معطّل. يمكن للمالك تفعيل الوضع التجريبي أو الوضع الحي المقيد.",
  mockNotice: "وضع تجريبي — معاينات بديلة فقط، ليست صور ذكاء اصطناعي حقيقية.",
  conceptPreviewOnly: "معاينات مفاهيم فقط — ليست ملفات طباعة نهائية. مراجعة المصمم والطباعة مطلوبة.",
  selectConcept: "اختيار", rejectConcept: "رفض",
  exportPack: "تصدير حزمة المعاينة", costEstimate: "تقدير التكلفة",
  artworkTab: "الأرت وورك / تسليم الطباعة", newArtworkPackage: "حزمة أرت وورك جديدة",
  packageType: "نوع الحزمة", completenessScore: "درجة الاكتمال",
  missingItems: "العناصر الناقصة", panelsSection: "خطة الأسطح",
  copySection: "كتل النصوص", dielineSection: "الدايلاين / القوالب",
  checklistSection: "قائمة فحص الطباعة", exportsSection: "التصديرات",
  uploadDieline: "رفع دايلاين",
  notPrintReady: "حزمة مساعدة للإنتاج — ليست جاهزة للطباعة تلقائيًا. مراجعة المصمم وما قبل الطباعة والمورّد مطلوبة. لا يوجد تحقق بمستوى CAD.",
  complianceTab: "الامتثال / الموافقة", newComplianceCase: "حالة امتثال جديدة",
  reviewType: "نوع المراجعة", finalGate: "البوابة النهائية", riskLevel: "مستوى الخطر",
  claimsSection: "الادعاءات", languageSection: "اللغة", signoffSection: "الاعتماد",
  gateSection: "البوابة النهائية", requestSignoff: "طلب اعتماد",
  notLegalApproval: "دعم قرار داخلي + اعتماد بشري. ليس استشارة قانونية أو شهادة تنظيمية/سلامة غذائية أو موافقة مورّد أو جاهزية طباعة. المراجعة البشرية مطلوبة.",
  suppliersTitle: "دليل المورّدين", supplierTab: "المورّدون / RFQ",
  newSupplier: "مورّد جديد", newRfq: "إنشاء RFQ", readiness: "الجاهزية",
  shortlist: "قائمة الملاءمة", quotesSection: "عروض الأسعار",
  comparisonSection: "المقارنة", recommendation: "مسودة توصية",
  humanDecision: "قرار بشري", fitScore: "الملاءمة",
  notSupplierApproval: "أداة دليل + RFQ + مقارنة أسعار داخلية. ليست موافقة مورّد أو شراء أو عقد أو دفع. الشهادات بيانات غير مُتحقق منها؛ التوصيات مسودات — القرار البشري مطلوب.",
  salesTab: "حزمة المبيعات", newSalesPackage: "إنشاء حزمة مبيعات",
  salesSections: "الأقسام", pricingBlocks: "كتل التسعير (عناصر نائبة)",
  reviewGate: "بوابة المراجعة", exportInternal: "تصدير داخلي",
  exportClientPreview: "تصدير معاينة العميل", exportCaseStudy: "تصدير دراسة حالة",
  readinessScore: "الجاهزية",
  clientPreviewSafety: "معاينة العميل تحمل علامة مائية وتستبعد الملاحظات الداخلية والهوامش وتفاصيل المورّد.",
  notCustomerApproval: "مسودة عرض احترافي داخلي. معاينة عميل / مفهوم فقط — ليست جاهزة للطباعة ولا عقدًا ولا فاتورة ولا موافقة عميل/قانونية/مورّد. المراجعة البشرية مطلوبة.",
  productionTab: "الإنتاج / QA", createWorkflow: "بدء سير عمل الإنتاج",
  qaChecklist: "قائمة فحص الجودة", qaGate: "بوابة الجودة", revisions: "المراجعات",
  finalDelivery: "التسليم النهائي", ownerTestReady: "جاهز لاختبار المالك",
  realTeamTestPlan: "خطة اختبار الفريق الحقيقي", addRevision: "إضافة مراجعة",
  approveOwnerTest: "اعتماد لاختبار المالك",
  internalWorkflowOnly: "سير عمل جودة داخلي فقط — ليس إطلاقًا عامًا/تجاريًا، ولا موافقة عميل/عقد/فاتورة/دفع، ولا موافقة قانونية/تنظيمية/مورّد/طباعة/إنتاج. المراجعة البشرية مطلوبة.",
  providerActivationPending: "تفعيل المزوّد خطوة منفصلة لاحقة معلّقة — هذه الخطة لا تفعّل أي مزوّد.",
};

const tr: StudioStrings = {
  prospectsTitle: "Potansiyel müşteriler", projectsTitle: "Ambalaj projeleri",
  newProspect: "Yeni müşteri adayı", newProject: "Yeni proje",
  nameLabel: "Ad", typeLabel: "Tür", statusLabel: "Durum",
  countryLabel: "Ülke", cityLabel: "Şehir", notesLabel: "Notlar",
  save: "Kaydet", archive: "Arşivle", searchLabel: "Ara",
  emptyList: "Henüz kayıt yok — ilkini oluşturun.",
  loadError: "Veriler yüklenemedi.", permissionDenied: "Bu işlem için yetkiniz yok.",
  briefTab: "Brif", referencesTab: "Referanslar", generationTab: "Tasarım üretim brifi",
  activityTab: "Etkinlik", overviewTab: "Genel bakış",
  completeness: "Brif tamlığı", missingFields: "Eksik alanlar",
  uploadReference: "Referans yükle", referenceType: "Referans türü",
  createGenerationBrief: "Tasarım üretim brifini derle",
  readiness: "Hazırlık", noImagePhase: "Bu aşamada görsel üretilmez — gelecek aşama için yapılandırılmış girdidir.",
  nextAction: "Önerilen sonraki adım", teamTitle: "İç ekip",
  inviteMember: "Ekip üyesi ekle", roleLabel: "Rol", providerLabel: "Giriş yöntemi",
  suspend: "Askıya al", reactivate: "Yeniden etkinleştir",
  googleLogin: "Google ile devam et", googleUnavailable: "Google girişi yapılandırılmadı — e-posta/şifre kullanın.",
  visualsTab: "Görsel konseptler", newConceptSet: "Yeni konsept seti",
  generateImages: "Konsept üret", imageCount: "Görsel sayısı",
  objectType: "Ambalaj nesnesi", conceptDirection: "Konsept yönü",
  styleDirection: "Stil yönü",
  disabledNotice: "Görsel üretimi kapalı. Sahip, mock veya kontrollü canlı modu etkinleştirebilir.",
  mockNotice: "MOCK modu — yalnızca yer tutucu önizlemeler, gerçek yapay zekâ görseli değildir.",
  conceptPreviewOnly: "Yalnızca konsept önizlemesi — baskıya hazır değildir. Tasarımcı ve baskı öncesi incelemesi gerekir.",
  selectConcept: "Seç", rejectConcept: "Reddet",
  exportPack: "Önizleme paketini dışa aktar", costEstimate: "Maliyet tahmini",
  artworkTab: "Artwork / Baskı teslimi", newArtworkPackage: "Yeni artwork paketi",
  packageType: "Paket türü", completenessScore: "Tamlık",
  missingItems: "Eksik öğeler", panelsSection: "Panel planı",
  copySection: "Metin blokları", dielineSection: "Dieline / şablonlar",
  checklistSection: "Baskı kontrol listesi", exportsSection: "Dışa aktarımlar",
  uploadDieline: "Dieline yükle",
  notPrintReady: "Üretim destek paketi — otomatik olarak baskıya hazır DEĞİLDİR. Tasarımcı, baskı öncesi ve tedarikçi incelemesi gerekir. CAD düzeyinde doğrulama yoktur.",
  complianceTab: "Uyumluluk / Onay", newComplianceCase: "Yeni uyumluluk vakası",
  reviewType: "İnceleme türü", finalGate: "Nihai kapı", riskLevel: "Risk düzeyi",
  claimsSection: "İddialar", languageSection: "Dil", signoffSection: "Onay",
  gateSection: "Nihai kapı", requestSignoff: "Onay iste",
  notLegalApproval: "Dahili karar desteği + insan onayı. Hukuki tavsiye, düzenleyici/gıda güvenliği sertifikası, tedarikçi veya baskı onayı DEĞİLDİR. İnsan incelemesi gerekir.",
  suppliersTitle: "Tedarikçi dizini", supplierTab: "Tedarikçi / RFQ",
  newSupplier: "Yeni tedarikçi", newRfq: "RFQ oluştur", readiness: "Hazırlık",
  shortlist: "Uygunluk listesi", quotesSection: "Teklifler",
  comparisonSection: "Karşılaştırma", recommendation: "Öneri taslağı",
  humanDecision: "İnsan kararı", fitScore: "Uygunluk",
  notSupplierApproval: "Dahili dizin + RFQ + teklif karşılaştırma aracı. Tedarikçi onayı, satın alma, sözleşme veya ödeme DEĞİLDİR. Sertifikalar doğrulanmamış meta veridir; öneriler TASLAKTIR — insan kararı gerekir.",
  salesTab: "Satış paketi", newSalesPackage: "Satış paketi oluştur",
  salesSections: "Bölümler", pricingBlocks: "Fiyat yer tutucuları",
  reviewGate: "İnceleme kapısı", exportInternal: "Dahili dışa aktar",
  exportClientPreview: "Müşteri önizlemesini dışa aktar", exportCaseStudy: "Vaka çalışması dışa aktar",
  readinessScore: "Hazırlık",
  clientPreviewSafety: "Müşteri önizlemesi filigranlıdır ve dahili notları, marjları ve tedarikçi ayrıntılarını hariç tutar.",
  notCustomerApproval: "Dahili profesyonel teklif taslağı. MÜŞTERİ ÖNİZLEMESİ / YALNIZCA KONSEPT — baskıya hazır değil, sözleşme değil, fatura değil, müşteri/hukuki/tedarikçi onayı değil. İnsan incelemesi gerekir.",
  productionTab: "Üretim / QA", createWorkflow: "Üretim iş akışını başlat",
  qaChecklist: "QA kontrol listesi", qaGate: "QA kapısı", revisions: "Revizyonlar",
  finalDelivery: "Nihai teslim", ownerTestReady: "Sahip testine hazır",
  realTeamTestPlan: "Gerçek ekip test planı", addRevision: "Revizyon ekle",
  approveOwnerTest: "Sahip testi için onayla",
  internalWorkflowOnly: "Yalnızca dahili QA iş akışı — kamusal/ticari lansman değil, müşteri/sözleşme/fatura/ödeme değil, hukuki/düzenleyici/tedarikçi/baskı/üretim onayı değil. İnsan incelemesi gerekir.",
  providerActivationPending: "Sağlayıcı etkinleştirme ayrı, sonraki, bekleyen bir adımdır — bu plan hiçbir sağlayıcıyı etkinleştirmez.",
};

const MAP: Record<LocaleCode, StudioStrings> = { en, fa, ar, tr };
export const studioStrings = (locale: LocaleCode): StudioStrings => MAP[locale] ?? en;
